import { stripColorCodes } from "./itemDisplayName";
import { itemKey, type CraftingResolverService } from "./craftingResolver";
import type { InventoryCustomItemDto, ItemStackDto } from "../api/generated/schemas";

// The item pool a Sellbot picker searches: physical chamber stock first, then the account's saved
// custom items (an offer for one with no physical copy on hand), then the item catalog (a plain
// vanilla item nobody has picked up yet). Shared so the single-item picker and the guided wizard
// search the same pool instead of the wizard silently missing custom items, as SellbotOffers.tsx once did.
const FALLBACK_MATCH_LIMIT = 40;

const stacksByCustomItem = new WeakMap<InventoryCustomItemDto, ItemStackDto>();

// Same object per registry row, so a re-run search hands <For> rows it can keep instead of rebuilding them.
export function fromCustomItem(item: InventoryCustomItemDto): ItemStackDto {
    const cached = stacksByCustomItem.get(item);
    if (cached) return cached;
    const stack: ItemStackDto = {
        key: itemKey(item.itemId, item.metadata, item.hash), itemId: item.itemId, metadata: item.metadata,
        count: 1, snbt: item.snbt ?? null, nbtHash: item.hash, isCustom: true,
    };
    stacksByCustomItem.set(item, stack);
    return stack;
}

// Resolving a display name parses the item's SNBT. A chamber with hundreds of distinct stacks made
// that expensive enough to freeze the tab in two different ways this went through before landing here:
// once by redoing it for every stock item on every keystroke, and once more by still doing it for every
// stock item up front to just browse the grid, before the operator had typed anything to search for.
// Browsing still reads no name at all, and a query resolves each stack once and keeps it, which also
// covers a live bot's frequent stock refreshes handing out a fresh ItemStackDto per event.
export function createSellbotNameCache(): Map<string, string> {
    return new Map();
}

export function cachedNameOf(
    nameCache: Map<string, string>,
    displayName: (item: ItemStackDto) => string,
): (item: ItemStackDto) => string {
    return (item: ItemStackDto) => {
        const cached = nameCache.get(item.key);
        if (cached !== undefined) return cached;
        const name = displayName(item).toLowerCase();
        nameCache.set(item.key, name);
        return name;
    };
}

// Every match, best first: the exact name, then names starting with the query, then names containing
// it, then the raw key, which can only match on the item id or the NBT hash. Ties keep the order the
// pool arrived in. Nothing is dropped: a picker that hides matches sends the operator hunting for an
// item they are looking straight at in their storage.
export function matchItemsByRelevance(
    query: string,
    items: readonly ItemStackDto[],
    nameOf: (item: ItemStackDto) => string,
): ItemStackDto[] {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return [...items];

    const ranked: { rank: number; position: number; item: ItemStackDto }[] = [];
    for (let position = 0; position < items.length; position++) {
        const item = items[position];
        const name = nameOf(item);
        const rank = name === q ? 0
            : name.startsWith(q) ? 1
                : name.includes(q) ? 2
                    : item.key.toLowerCase().includes(q) ? 3
                        : -1;
        if (rank >= 0) ranked.push({ rank, position, item });
    }
    ranked.sort((left, right) => left.rank - right.rank || left.position - right.position);
    return ranked.map(entry => entry.item);
}

export function searchSellbotItems(
    query: string,
    stock: readonly ItemStackDto[],
    customItems: readonly InventoryCustomItemDto[],
    resolver: CraftingResolverService | null | undefined,
    displayName: (item: ItemStackDto) => string,
    nameCache: Map<string, string>,
): ItemStackDto[] {
    const q = query.trim().toLowerCase();
    const out: ItemStackDto[] = [];
    const seen = new Set<string>();
    const add = (item: ItemStackDto) => { if (!seen.has(item.key)) { seen.add(item.key); out.push(item); } };

    for (const item of matchItemsByRelevance(q, stock, cachedNameOf(nameCache, displayName))) add(item);

    // The registry and the catalog are every item anyone has seen or that exists, rather than a pool
    // the operator owns, so both stay bounded: they are the fallback for an item not in stock, not the answer.
    if (q) {
        let customMatches = 0;
        for (const entry of customItems) {
            if (customMatches >= FALLBACK_MATCH_LIMIT) break;
            // Every signed copy of one product is its own registry row; the Sellbot matcher ignores the
            // serial anyway, so one copy stands for all of them and for stock from a code-ignoring storage.
            const productKey = itemKey(entry.itemId, entry.metadata, entry.productHash);
            if (seen.has(productKey)) continue;
            const name = stripColorCodes(entry.displayName ?? "").toLowerCase();
            const lore = stripColorCodes(entry.lore ?? "").toLowerCase();
            const item = fromCustomItem(entry);
            if (!name.includes(q) && !lore.includes(q) && !item.key.toLowerCase().includes(q)) continue;
            seen.add(productKey);
            add(item);
            customMatches++;
        }
        if (resolver)
            for (const def of resolver.searchItems(q).slice(0, FALLBACK_MATCH_LIMIT))
                add({ key: def.key, itemId: def.id, metadata: def.metadata, count: 1, snbt: null, nbtHash: null, isCustom: false });
    }

    return out;
}
